import { applyTheme, resolveTheme, storedTheme, transitionTheme } from "./preferences.js";
import { t as ThemeToggle } from "./theme-toggle-gP7w4XRa.js";
import { useEffect, useState } from "react";
import { getI18n } from "react-i18next";
import { jsx } from "react/jsx-runtime";
import { MantineProvider, createTheme, defaultVariantColorsResolver } from "@mantine/core";
import { flushSync } from "react-dom";
// src/theme/palette.ts
const tuple = (shades) => shades.split(" ");
/** Ten-shade Mantine tuples of the HLT palette (gray and dark are the warm neutrals). */
const paletteColors = {
	hlt: tuple("#ebfaec #d2f4d6 #afebb6 #84e093 #52d46f #00c24e #0c953c #006b2e #034e1c #01330f"),
	green: tuple("#ebf9f1 #d4f2e2 #b1e8cb #87dcb3 #59d09c #1dbe84 #0c9264 #216e4e #034c32 #01321f"),
	teal: tuple("#eaf9f8 #d1f1ef #abe6e3 #7edad6 #46cdc8 #13b9b5 #0c8e8b #066663 #034a48 #01302f"),
	cyan: tuple("#eaf8fe #d1effd #ade2fb #81d4f8 #4fc5f3 #13b1e4 #0c88b0 #06617f #03465d #012e3d"),
	blue: tuple("#f0f6ff #deeaff #c4dafe #a8c9fe #8bb7fe #69a1fd #3777e1 #175cd3 #0f3b82 #04255b"),
	indigo: tuple("#f3f4ff #e6e8ff #d2d5fe #bec1fe #aaadfe #9493fd #6f6ada #4f49a5 #38337d #231e57"),
	violet: tuple("#f8f2ff #efe4ff #e3cefe #d7b6fe #cb9efe #ba82f5 #925fc5 #6a4094 #4e2b70 #34194d"),
	pink: tuple("#fff0f9 #ffdff3 #fdc6e9 #faa9df #f48ed4 #e572c2 #b65099 #873370 #652153 #451137"),
	rose: tuple("#fff1f2 #ffe1e4 #fec9ce #feaeb7 #fe919f #f86d85 #c64c63 #942f44 #6f1d2f #4c0d1c"),
	red: tuple("#fff2f0 #ffe2dd #fecbc3 #feb1a5 #fe9485 #fd6d5d #cf4639 #b42318 #751711 #510805"),
	orange: tuple("#fff2eb #ffe3d4 #fecdb1 #feb487 #fc9a5b #ed8032 #bd5d0a #894204 #652e02 #431d01"),
	amber: tuple("#fcf4e8 #f9e7cd #f4d4a6 #eebe7a #e6a949 #d59310 #a47009 #925400 #563902 #392401"),
	yellow: tuple("#f9f5e7 #f2eacb #e8d9a2 #ffc933 #d2b438 #be9f10 #927a09 #695705 #4c3f02 #322801"),
	gray: tuple("#fbf8f1 #f3eee2 #ece7db #e2dccc #d8d0be #b0ab9c #7d7a6c #696c63 #4e5a52 #10231a"),
	dark: tuple("#efede5 #d0cec7 #b6b4ad #76746f #33322e #2c2b27 #191916 #121210 #0e0d0b #0a0907")
};
"#2d6fcd #e16c10 #0e9a96 #7145b5 #0b8c38 #79368d #b0850c #c34e97".split(" "), "#4385e4 #d8670c #0e9a96 #946ade #229944 #9e59b3 #b78a0d #ca549d".split(" ");
// src/theme/provider.tsx
/** Mantine owns component behavior; existing semantic tokens remain the brand source of truth. */
const hltTheme = createTheme({
	fontFamily: "'IBM Plex Sans', sans-serif",
	fontFamilyMonospace: "'IBM Plex Mono', monospace",
	primaryColor: "hlt",
	breakpoints: {
		xs: "36em",
		sm: "40em",
		md: "48em",
		lg: "64em",
		xl: "80em"
	},
	colors: paletteColors,
	primaryShade: {
		light: 7,
		dark: 5
	},
	defaultRadius: "sm",
	radius: {
		xs: "0.25rem",
		sm: "0.25rem",
		md: "0.5rem",
		lg: "0.75rem",
		xl: "1.5rem"
	},
	headings: {
		fontFamily: "'IBM Plex Sans', sans-serif",
		fontWeight: "600"
	},
	respectReducedMotion: true,
	cursorType: "pointer",
	activeClassName: "hlt-press",
	variantColorResolver: (input) => {
		const defaults = defaultVariantColorsResolver(input);
		const quiet = [
			"light",
			"subtle",
			"transparent",
			"outline"
		].includes(input.variant);
		if (!input.color && quiet) return {
			...defaults,
			background: input.variant === "light" ? "var(--muted)" : "transparent",
			hover: input.variant === "light" ? "var(--nav-active)" : "var(--muted)",
			color: "var(--foreground)",
			border: input.variant === "outline" ? "1px solid var(--border)" : "1px solid transparent"
		};
		const primary = !input.color || input.color === "hlt";
		const danger = input.color === "red";
		if (!primary && !danger) return defaults;
		const color = primary ? "var(--primary-text)" : "var(--destructive)";
		const surface = primary ? "var(--brand-subtle)" : "var(--destructive-surface)";
		if (input.variant === "filled") return {
			...defaults,
			background: primary ? "var(--primary)" : "var(--destructive-solid)",
			hover: primary ? "var(--primary-hover)" : "color-mix(in srgb, var(--destructive-solid) 90%, var(--foreground))",
			color: primary ? "var(--primary-foreground)" : "var(--destructive-solid-foreground)"
		};
		if ([
			"light",
			"subtle",
			"transparent",
			"outline"
		].includes(input.variant)) return {
			...defaults,
			background: input.variant === "light" ? surface : "transparent",
			hover: surface,
			color: input.variant === "light" && primary ? "var(--brand-subtle-foreground)" : color,
			border: input.variant === "outline" ? `1px solid ${color}` : "1px solid transparent"
		};
		return defaults;
	},
	defaultGradient: {
		from: "hlt",
		to: "hlt",
		deg: 0
	},
	components: {
		Checkbox: { defaultProps: { size: "md" } },
		Radio: { defaultProps: { size: "md" } },
		Button: { defaultProps: { size: "md" } },
		SegmentedControl: { defaultProps: { size: "md" } },
		Avatar: { defaultProps: {
			component: "span",
			size: 32,
			color: "hlt",
			alt: "",
			"aria-hidden": true,
			imageProps: { referrerPolicy: "no-referrer" }
		} },
		TextInput: { defaultProps: { size: "md" } },
		NumberInput: { defaultProps: {
			size: "md",
			hideControls: true
		} },
		ColorInput: { defaultProps: { size: "md" } },
		ColorPicker: { defaultProps: () => ({
			hueLabel: getI18n().t("common.colorHue"),
			saturationLabel: getI18n().t("common.colorSaturation")
		}) },
		Slider: { defaultProps: { thumbSize: 44 } },
		NativeSelect: { defaultProps: { size: "md" } },
		Textarea: { defaultProps: { size: "md" } },
		Modal: { defaultProps: {
			centered: true,
			transitionProps: {
				duration: 180,
				transition: "fade"
			}
		} },
		Drawer: { defaultProps: { transitionProps: { duration: 200 } } },
		Menu: { defaultProps: { transitionProps: {
			duration: 120,
			transition: "pop-top-right"
		} } }
	}
});
const variables = () => ({
	variables: {
		"--mantine-color-body": "var(--background)",
		"--mantine-color-text": "var(--foreground)",
		"--mantine-color-dimmed": "var(--muted-foreground)",
		"--mantine-color-default": "var(--card)",
		"--mantine-color-default-color": "var(--foreground)",
		"--mantine-color-default-border": "var(--border)",
		"--mantine-color-default-hover": "var(--muted)",
		"--mantine-color-placeholder": "var(--subtle-foreground)",
		"--mantine-color-anchor": "var(--primary-text)",
		"--mantine-primary-color-filled": "var(--primary)",
		"--mantine-primary-color-filled-hover": "var(--primary-hover)",
		"--mantine-primary-color-contrast": "var(--primary-foreground)",
		"--mantine-primary-color-light": "var(--brand-subtle)",
		"--mantine-primary-color-light-color": "var(--brand-subtle-foreground)",
		"--mantine-primary-color-light-hover": "color-mix(in oklab, var(--brand-subtle) 85%, var(--primary))"
	},
	light: {},
	dark: {}
});
/**
* Follows the shared browser preference (`@hlt/web-kit/preferences`) and the operating system. The initial
* scheme is resolved synchronously, matching what `/hlt-boot.js` applied before the first paint.
*/
function HltProvider({ children, staticMode = false }) {
	const [scheme, setScheme] = useState(() => staticMode ? "light" : resolveTheme());
	useEffect(() => {
		if (staticMode) return;
		const media = window.matchMedia("(prefers-color-scheme: dark)");
		const current = () => {
			const explicit = document.documentElement.dataset.theme;
			return explicit === "light" || explicit === "dark" ? explicit : resolveTheme(storedTheme());
		};
		const sync = () => flushSync(() => setScheme(current()));
		const fromElsewhere = () => {
			const preference = storedTheme();
			if (preference !== (document.documentElement.dataset.theme ?? "system")) applyTheme(preference, { persist: false });
		};
		const onVisible = () => {
			if (document.visibilityState === "visible") fromElsewhere();
		};
		const observer = new MutationObserver(sync);
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["data-theme"]
		});
		media.addEventListener("change", sync);
		window.addEventListener("storage", fromElsewhere);
		window.addEventListener("pageshow", fromElsewhere);
		document.addEventListener("visibilitychange", onVisible);
		setScheme(current());
		return () => {
			observer.disconnect();
			media.removeEventListener("change", sync);
			window.removeEventListener("storage", fromElsewhere);
			window.removeEventListener("pageshow", fromElsewhere);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [staticMode]);
	return /* @__PURE__ */ jsx(MantineProvider, {
		theme: hltTheme,
		cssVariablesResolver: variables,
		forceColorScheme: scheme,
		withGlobalClasses: !staticMode,
		children
	});
}
export { HltProvider, ThemeToggle, applyTheme, resolveTheme, storedTheme, transitionTheme };

//# sourceMappingURL=theme.js.map